import type { AttendanceGroup } from "../types/attendance";
import { resolveAttendanceImageUrl } from "./attendanceImageUrl";

/** Match preview modal image size (200px) at 2× for a sharp PNG. */
const DISPLAY_IMG_PX = 200;
const EXPORT_SCALE = 2;
const IMG_PX = DISPLAY_IMG_PX * EXPORT_SCALE;
/** Bootstrap `border-3` is 3px; scaled for export. */
const BORDER_PX = 3 * EXPORT_SCALE;
const BORDER_RADIUS_PX = 6 * EXPORT_SCALE;
/** Bootstrap `g-4` spacing between preview tiles. */
const GAP_PX = 24 * EXPORT_SCALE;
const PADDING_PX = 32 * EXPORT_SCALE;
const COLS = 3;
const PRIMARY_COLOR = "#0d6efd";

function injectSvgRasterSize(svgText: string, size: number): string {
  return svgText.replace(/<svg([^>]*)>/i, (_match, attrs: string) => {
    const cleaned = attrs.replace(/\swidth="[^"]*"/gi, "").replace(/\sheight="[^"]*"/gi, "");
    return `<svg${cleaned} width="${size}" height="${size}">`;
  });
}

function rasterizeImage(objectUrl: string, rasterPx: number): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const sourceW = image.naturalWidth || rasterPx;
      const sourceH = image.naturalHeight || rasterPx;
      const scale = Math.max(rasterPx / Math.max(sourceW, sourceH), 1);
      const canvasW = Math.round(sourceW * scale);
      const canvasH = Math.round(sourceH * scale);

      const canvas = document.createElement("canvas");
      canvas.width = canvasW;
      canvas.height = canvasH;
      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Canvas unavailable"));
        return;
      }

      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvasW, canvasH);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(image, 0, 0, canvasW, canvasH);
      resolve(canvas);
    };
    image.onerror = () => reject(new Error("Image render failed"));
    image.src = objectUrl;
  });
}

async function loadImageCanvas(url: string): Promise<HTMLCanvasElement> {
  const resolved = resolveAttendanceImageUrl(url);
  const response = await fetch(resolved, { credentials: "omit", mode: "cors" });
  if (!response.ok) {
    throw new Error("Failed to load image");
  }

  const blob = await response.blob();
  const isSvg =
    blob.type === "image/svg+xml" || resolved.toLowerCase().includes(".svg");

  const rasterBlob = isSvg
    ? new Blob([injectSvgRasterSize(await blob.text(), IMG_PX)], {
        type: "image/svg+xml",
      })
    : blob;

  const objectUrl = URL.createObjectURL(rasterBlob);
  try {
    return await rasterizeImage(objectUrl, IMG_PX);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function drawRoundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const r = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + width, y, x + width, y + height, r);
  context.arcTo(x + width, y + height, x, y + height, r);
  context.arcTo(x, y + height, x, y, r);
  context.arcTo(x, y, x + width, y, r);
  context.closePath();
}

/** One bordered tile matching preview: border wraps the image, not an empty cell. */
function renderBorderedTile(imageCanvas: HTMLCanvasElement): HTMLCanvasElement {
  const tileSize = IMG_PX + BORDER_PX * 2;
  const tile = document.createElement("canvas");
  tile.width = tileSize;
  tile.height = tileSize;
  const context = tile.getContext("2d");
  if (!context) {
    throw new Error("Canvas unavailable");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, tileSize, tileSize);

  const scale = Math.min(IMG_PX / imageCanvas.width, IMG_PX / imageCanvas.height);
  const drawW = Math.round(imageCanvas.width * scale);
  const drawH = Math.round(imageCanvas.height * scale);
  const drawX = BORDER_PX + Math.round((IMG_PX - drawW) / 2);
  const drawY = BORDER_PX + Math.round((IMG_PX - drawH) / 2);
  context.drawImage(imageCanvas, drawX, drawY, drawW, drawH);

  const inset = BORDER_PX / 2;
  context.strokeStyle = PRIMARY_COLOR;
  context.lineWidth = BORDER_PX;
  drawRoundedRect(
    context,
    BORDER_PX + inset,
    BORDER_PX + inset,
    IMG_PX - BORDER_PX,
    IMG_PX - BORDER_PX,
    BORDER_RADIUS_PX,
  );
  context.stroke();

  return tile;
}

export async function downloadGroupsAsImage(groups: AttendanceGroup[]): Promise<void> {
  const imagesWithUrl = groups.filter((group) => group.correctImageUrl);
  if (imagesWithUrl.length === 0) {
    throw new Error("No images available to download");
  }

  const tiles = (await Promise.all(
    imagesWithUrl.map((group) => loadImageCanvas(group.correctImageUrl!)),
  )).map(renderBorderedTile);

  const tileSize = IMG_PX + BORDER_PX * 2;
  const cols = Math.min(COLS, tiles.length);
  const rows = Math.ceil(tiles.length / cols);
  const width = PADDING_PX * 2 + cols * tileSize + (cols - 1) * GAP_PX;
  const height = PADDING_PX * 2 + rows * tileSize + (rows - 1) * GAP_PX;

  const sheet = document.createElement("canvas");
  sheet.width = width;
  sheet.height = height;
  const context = sheet.getContext("2d");
  if (!context) {
    throw new Error("Canvas unavailable");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);

  tiles.forEach((tile, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);
    const x = PADDING_PX + col * (tileSize + GAP_PX);
    const y = PADDING_PX + row * (tileSize + GAP_PX);
    context.drawImage(tile, x, y);
  });

  await new Promise<void>((resolve, reject) => {
    sheet.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to create image"));
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = "attendance-group-images.png";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
      resolve();
    }, "image/png");
  });
}
